import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  AlertBanner,
  Button,
  ButtonIcon,
  Icon,
  InputField,
  Typography,
} from '@saviynt/design-system';

import { useLocalization } from '../../../../../hooks';
import type { CopilotState } from '../../../data-layer/types';
import { setAddAccessInputField } from '../../../data-store/copilotSlice';
import { useAddAccessLogic } from '../../../hooks/addAccessLogic';
import { useChatFlowController } from '../../../hooks/useChatFlowController';
import ExpandableForm from '../../ExpandableForm/ExpandableForm';
import Responses from '../../Responses/Responses';

import './AddAccess.css';

/**
 * AddAccess
 */
function AddAccess() {
  const dispatch = useDispatch();
  const addAccessInputField = useSelector(
    (state: { copilot: CopilotState }) => state.copilot.ui.addAccessInputField
  );

  const { handleFormSubmit, handleSkip } = useChatFlowController();
  const formatMessage = useLocalization();

  // Get messages to check for existing entitlement forms
  const messages = useSelector(
    (state: { copilot: CopilotState }) => state.copilot.messages
  );

  const {
    entitlements,
    applicationName,
    isVisible,
    isSkipped,
    handleChange,
    handleAddEntitlement,
    handleRemoveEntitlement,
    handleSave: originalHandleSave,
    setIsSkipped,
    onCancel,
  } = useAddAccessLogic(handleFormSubmit);

  const handleSave = () => {
    dispatch(setAddAccessInputField(true));
    originalHandleSave();
  };

  const handleFormSkip = () => {
    // Check if there are existing AddAccess entitlement forms in messages
    const hasEntitlementForms = messages.some(
      (msg) => msg.subType === 'accessAttributeMapping'
    );

    // Always collapse the form first
    setIsSkipped(true);

    // If entitlement forms exist, just collapse the form (don't call handleSkip)
    if (hasEntitlementForms) {
      return;
    }

    // If no entitlement forms, do the full skip navigation
    handleSkip('AddAccess', 'Confirmation');
  };

  const isEditMode = useSelector(
    (state: { copilot: CopilotState }) => state.copilot.ui.mode === 'edit'
  );

  const extraContent = (
    <div style={{ padding: '0.75rem' }}>
      <Button
        type='button'
        kind='ghost'
        size='large'
        onClick={handleAddEntitlement}
        isDisabled={addAccessInputField}
        leftIcon={<Icon kind='Add' />}>
        {formatMessage(
          'INTEGRATIONS.COPILOT.ADD_ACCESS_ADD_NEW_ENTITLEMENT_TYPE',
          'Add Entitlement Type'
        )}
      </Button>
      <AlertBanner
        colorTheme='Warning'
        kind='low'
        title={formatMessage(
          'INTEGRATIONS.COPILOT.MAX_ENTITLEMENTS',
          'Maximum Entitlement',
          { MAX_ENTITLEMENTS: 6 }
        )}
        description={formatMessage(
          'INTEGRATIONS.COPILOT.MAX_ENTITLEMENTS_ADDED',
          'Maximum Entitlement Added'
        )}
        isVisible={isVisible}
        onCancel={onCancel}
      />
    </div>
  );

  return (
    <div>
      {isEditMode ? (
        <Responses type='addAccessInfoEditMode' />
      ) : (
        <Responses values={[applicationName]} type='accessAccountMessage' />
      )}
      <ExpandableForm
        classname='AddAccess'
        isCollapseable
        heading={formatMessage(
          'INTEGRATIONS.COPILOT.STEP_ADD_ACCESS',
          'Add Access'
        )}
        buttonkind='filled'
        buttonLabel={formatMessage(
          'INTEGRATIONS.FILEUPLOAD.CONTINUE',
          'Continue'
        )}
        extraBody={extraContent}
        secondryButtonKind={!isEditMode ? 'outlined' : undefined}
        secondryButtonLabel={
          !isEditMode
            ? formatMessage('INTEGRATIONS.COPILOT.SKIP', 'Skip')
            : undefined
        }
        stepName='AddAccess'
        isSecondaryDisabled={isSkipped || isEditMode || addAccessInputField}
        isDisabled={addAccessInputField}
        onSecondory={!isEditMode ? handleFormSkip : undefined}
        onSave={handleSave}>
        <div>
          {entitlements.map((ent, index) => (
            <div key={ent.id} className='AddAccess-EntitlementType'>
              <InputField
                name={`entitlement-${index}`}
                isDisabled={addAccessInputField}
                placeholder={formatMessage(
                  'INTEGRATIONS.COPILOT.ADD_ACCESS_ENTER_ENTITLEMENT_TYPE',
                  'Enter Entitlement type'
                )}
                label={
                  <Typography kind='label' htmlFor={`entitlement-${index}`}>
                    {formatMessage(
                      'INTEGRATIONS.COPILOT.ADD_ACCESS_ENTITLEMENT_TYPE',
                      'Entitlement type'
                    )}
                  </Typography>
                }
                value={ent.value}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  handleChange(index, e.target.value);
                }}
              />
              {entitlements.length > 1 && (
                <ButtonIcon
                  className='AddAccess-EntitlementType-Icon'
                  size='small'
                  kind='outlinedNeutral'
                  icon={<Icon kind='delete' size='medium' />}
                  onClick={() => handleRemoveEntitlement(index)}
                />
              )}
            </div>
          ))}
        </div>
      </ExpandableForm>
    </div>
  );
}

export default AddAccess;
